/**
 * Converts an LLM's [POINT:screen=N,x=X,y=Y] coordinates (in screenshot
 * pixel space) back to global desktop POINTS using the metadata that
 * was sent with the original screenshot.
 *
 * Coordinate spaces involved:
 *   1. Screenshot pixels  — what the LLM sees, e.g. (0..1280, 0..800)
 *   2. Native display px  — the display's actual pixel grid, e.g. 2880x1800
 *   3. Display points     — AppKit logical units (px / scaleFactor)
 *   4. Global desktop pts — point coords + display origin
 *
 * The frontend then subtracts its own window origin (per Tauri overlay
 * window) to get window-local coords for rendering.
 */

export interface ScreenshotScreenMeta {
  index: number; // 0-based; LLM sees screen=index+1
  displayId: number;
  /** Native capture hint. Older stored metadata may not include it. */
  isCursorScreen?: boolean;
  scaleFactor: number; // 1 or 2 typically
  originX: number; // CG top-left, in points
  originY: number;
  /** Exact captured extent in global desktop points. */
  widthPoints?: number;
  heightPoints?: number;
  widthPx: number; // native pixels
  heightPx: number;
  scaledWidth: number; // JPEG dimensions sent to LLM
  scaledHeight: number;
}

export interface AgentPoint {
  screen: number; // 1-based as the LLM sees it
  x: number; // screenshot pixel coords
  y: number;
}

export interface AgentRect extends AgentPoint {
  width: number;
  height: number;
}

export interface ResolvedPoint {
  displayId: number;
  globalX: number; // global desktop points, top-left origin
  globalY: number;
}

export interface ResolvedRect extends ResolvedPoint {
  width: number;
  height: number;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function clampScreenshotPoint(v: number, extent: number): number {
  // Screenshot pixels are a half-open grid. Mapping x === width (or
  // y === height) to desktop points lands on the next display/window.
  // Keep even malformed edge coordinates at the center of the last pixel.
  // A machine epsilon can disappear when the display origin is added.
  return clamp(v, 0, Math.max(0, extent - 0.5));
}

function isPositiveFinite(v: number): boolean {
  return Number.isFinite(v) && v > 0;
}

function screenPointExtent(
  points: number | undefined,
  nativePixels: number,
  scaleFactor: number,
): number {
  return isPositiveFinite(points ?? 0) ? points! : nativePixels / scaleFactor;
}

export function resolveAgentPoint(
  point: AgentPoint | null,
  screens: ScreenshotScreenMeta[],
): ResolvedPoint | null {
  return resolveAgentPointWithinBounds(point, screens, false);
}

function resolveAgentPointWithinBounds(
  point: AgentPoint | null,
  screens: ScreenshotScreenMeta[],
  includeFarEdge: boolean,
): ResolvedPoint | null {
  if (!point) return null;
  // Attachment order is canonical. Recover a copied native ID only when the
  // current capture metadata identifies exactly one image on that display.
  const ordinalScreen = screens.find((s) => s.index + 1 === point.screen);
  const displayMatches = ordinalScreen
    ? []
    : screens.filter((s) => s.displayId === point.screen);
  const screen =
    ordinalScreen ??
    (displayMatches.length === 1 ? displayMatches[0] : undefined);
  if (!screen) return null;
  if (
    !isPositiveFinite(screen.scaleFactor) ||
    !isPositiveFinite(screen.widthPx) ||
    !isPositiveFinite(screen.heightPx) ||
    !isPositiveFinite(screen.scaledWidth) ||
    !isPositiveFinite(screen.scaledHeight) ||
    !Number.isFinite(screen.displayId) ||
    !Number.isFinite(screen.originX) ||
    !Number.isFinite(screen.originY)
  ) {
    return null;
  }
  const px = includeFarEdge
    ? clamp(point.x, 0, screen.scaledWidth)
    : clampScreenshotPoint(point.x, screen.scaledWidth);
  const py = includeFarEdge
    ? clamp(point.y, 0, screen.scaledHeight)
    : clampScreenshotPoint(point.y, screen.scaledHeight);
  // Prefer the logical bounds captured for this exact display. Deriving
  // points from backing pixels / scaleFactor is only a compatibility path:
  // macOS explicitly allows those values to describe different geometries
  // in scaled and mirrored display modes.
  const widthPoints = screenPointExtent(
    screen.widthPoints,
    screen.widthPx,
    screen.scaleFactor,
  );
  const heightPoints = screenPointExtent(
    screen.heightPoints,
    screen.heightPx,
    screen.scaleFactor,
  );
  const pointX = (px / screen.scaledWidth) * widthPoints;
  const pointY = (py / screen.scaledHeight) * heightPoints;
  return {
    displayId: screen.displayId,
    globalX: screen.originX + pointX,
    globalY: screen.originY + pointY,
  };
}

/**
 * Inverse of {@link resolveAgentPoint} for a single axis: converts a global
 * desktop POINT coordinate to the screenshot pixel space the LLM sees.
 * Used to build the `[POINTER:screen=N,x=...,y=...]` token that tells the
 * agent where the user's cursor is inside the attached screenshot.
 */
export function cursorToScreenshotPixel(
  cursor: number,
  origin: number,
  scaleFactor: number,
  nativeSizePx: number,
  screenshotSizePx: number,
  screenSizePoints?: number,
): number {
  if (nativeSizePx <= 0 || screenshotSizePx <= 0 || scaleFactor <= 0) {
    return 0;
  }
  const pointExtent = screenPointExtent(
    screenSizePoints,
    nativeSizePx,
    scaleFactor,
  );
  const localPoints = cursor - origin;
  const screenshotPixels = (localPoints / pointExtent) * screenshotSizePx;
  return Math.round(Math.max(0, Math.min(screenshotSizePx, screenshotPixels)));
}

export function resolveAgentRect(
  rect: AgentRect | null,
  screens: ScreenshotScreenMeta[],
): ResolvedRect | null {
  if (!rect) return null;
  // Rectangle endpoints describe extents rather than input locations, so the
  // far edge may equal the screenshot width/height without becoming a click.
  const start = resolveAgentPointWithinBounds(
    { screen: rect.screen, x: rect.x, y: rect.y },
    screens,
    true,
  );
  const end = resolveAgentPointWithinBounds(
    {
      screen: rect.screen,
      x: rect.x + rect.width,
      y: rect.y + rect.height,
    },
    screens,
    true,
  );
  if (!start || !end || start.displayId !== end.displayId) return null;
  const minX = Math.min(start.globalX, end.globalX);
  const minY = Math.min(start.globalY, end.globalY);
  return {
    displayId: start.displayId,
    globalX: minX,
    globalY: minY,
    width: Math.abs(end.globalX - start.globalX),
    height: Math.abs(end.globalY - start.globalY),
  };
}
